// ---------------------------------------------------------------------------
// Block Definition: conditional
//
// Renders one of several branches based on a discriminator value in
// crystallized data.  The LLM fills the discriminator field; the renderer
// evaluates conditions and selects the matching branch.
// ---------------------------------------------------------------------------

function nullable(type, description, extra = {}) {
  const t = Array.isArray(type) ? type : [type, "null"];
  return { type: t, ...(description ? { description } : {}), ...extra };
}

export const conditionalDef = {
  type: "conditional",
  label: "Conditional",
  description: "Shows or hides content based on a discriminator value.",

  crystallizer: {
    extractionPhases: ["llm"],

    buildDataSchema(block, opts) {
      const hints = opts.hints || [];
      const ownedKeys = new Set(hints.map(h => h.key).filter(Boolean));
      const field = block.branches?.[0]?.when?.field;
      if (!field) return { type: "object" };
      if (ownedKeys.size > 0 && !ownedKeys.has(field)) return null;
      const values = (block.branches || []).map(b => b.when?.value);
      const allBool = values.every(v => typeof v === "boolean");
      const desc = block.discriminator?.prompt || `Discriminator for ${block.id}`;
      const prop = allBool
        ? { type: "boolean", description: desc }
        : { type: "string", enum: values.filter(v => v !== undefined && v !== null).map(String), description: desc };
      return { type: "object", properties: { [field]: prop }, required: [field], additionalProperties: false };
    },

    buildShapeDescription(block) {
      const field = block.branches?.[0]?.when?.field;
      const values = (block.branches || []).map(b => JSON.stringify(b.when?.value)).filter(Boolean).join("|");
      return field ? `{ "${field}": <${values || "value"}> }` : `{ "<discriminator>": <value> }`;
    },

    isBlockLevelTarget() { return false; },
    skipFromPinSchema() { return false; },
    isCodeDerived() { return false; },
    collectHints() { return []; },
    buildPromptSupplement() { return ""; },
    postProcess(data) { return data; },
  },

  renderer: null, // wired by ui/src/blockRegistryInit.js

  editor: {
    icon: "git-branch",
    category: "layout",
    defaultTemplate: {
      type: "conditional",
      branches: [
        { when: { field: "flag", op: "eq", value: true }, then: { type: "text_block", id: "yes_branch", slots: [] } },
        { when: { field: "flag", op: "eq", value: false }, then: { type: "text_block", id: "no_branch", slots: [] } },
      ],
    },
    configFields: [
      { name: "branches", type: "array", label: "Branches",
        itemSchema: { fields: [
          { name: "when", type: "object", label: "Condition",
            fields: [
              { name: "field", type: "string", label: "Field name" },
              { name: "op", type: "select", label: "Operator", options: ["eq", "neq", "gt", "lt", "gte", "lte", "in"], defaultValue: "eq" },
              { name: "value", type: "string", label: "Value" },
            ]
          },
        ]}
      },
    ],
  },
};
